/**
 * "1–50 of 19,937", and the two buttons that move through it.
 *
 * Extracted because it was about to exist four times. Report pickup and the KT Insights export
 * history each grew their own copy — identical bar the wrapper's inline styles — and adding paging
 * to run history and the two subscription lists would have made four. Four copies of a control
 * whose entire job is to agree with itself about what page you are on is how one list ends up
 * saying "51–100 of 200" while another says "50–99".
 *
 * <b>The total is required and is the point.</b> A pager that only knows whether another page
 * exists can say "Next" but cannot say how much is there, and on run history "how much is there"
 * IS the finding — 19,937 rows is why the page was slow. So every caller's endpoint has to return a
 * count alongside the page, and this signature is what forces that.
 */
export function Pager({ skip, take, shown, total, busy, onChange }: {
  skip: number;
  take: number;
  /**
   * How many rows this page actually rendered, NOT the page size. The two differ on the last page,
   * and using `take` here would have the final page claim rows it never showed.
   */
  shown: number;
  total: number;
  /** Renders "updating…" rather than disabling the row, so a refetch does not make the page jump. */
  busy: boolean;
  onChange: (skip: number) => void;
}) {
  // Nothing to page through. Deliberately not "no rows" — an empty result already says so in the
  // table body, and a pager reading "0–0 of 0" underneath it is just a second way of saying it.
  if (total === 0) return null;

  const first = skip + 1;
  const last = skip + shown;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, marginTop: 12, fontSize: '.85rem', color: 'var(--kt-grey-500)'
    }}>
      <div>
        {first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()}
        {busy && ' · updating…'}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="btn-secondary btn-sm"
          disabled={skip === 0 || busy}
          onClick={() => onChange(Math.max(skip - take, 0))}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn-secondary btn-sm"
          // `last >= total` rather than a page count, so the last page disables Next even when the
          // total is not an exact multiple of the page size.
          disabled={last >= total || busy}
          onClick={() => onChange(skip + take)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
